import {ComponentInterface, ResponseObject} from "types/chat";
import {ChatType, MessageSendState} from "types/enums";
import {MessageHistory} from "classes/chat/messageHistory";
import Alpine from "@alpinejs/csp";
import {ChatWidget} from "classes/chat/chatWidget";
import {FEEDBACK_RATING_SUBMIT_EVENT, FEEDBACK_REASON_SUBMIT_EVENT, RatingEvent, ReasonEvent} from "types/events";
import {ChatFeedbackHandler} from "classes/chat/chatFeedbackHandler";
import BotChat from "classes/botChat";
import {WidgetConfig} from "types/config";
import ComponentConfig from "../../config/componentConfig";
import {ClientErrorLogger} from "../utils/clientErrorLogger";

export default class AlpineComponent implements ComponentInterface {

    private readonly clientErrorLogger: ClientErrorLogger;
    private alpineComponent: null|Element = null;
    private answerCounter: number = 1;
    private chatFeedbackHandler: ChatFeedbackHandler | null = null;

    public constructor(
        private readonly widget: ChatWidget,
        private readonly widgetDom: ShadowRoot,
        private readonly widgetConfig: WidgetConfig,
        private readonly componentConfig: ComponentConfig
    ) {
        this.initEventListeners();
        this.clientErrorLogger = new ClientErrorLogger();
    }

    private initEventListeners(): void {
        const alpineElement = this.getAlpineComponentElement();
        if (null === alpineElement) {
            return;
        }

        const onFeedbackRatingSubmitHandler = async (event: RatingEvent) => {
            const chatFeedbackHandler = this.ChatFeedbackHandler;
            await this.onFeedbackRatingSubmit(event, chatFeedbackHandler);
        };

        const onFeedbackReasonSubmitHandler = async (event: ReasonEvent) => {
            const chatFeedbackHandler = this.ChatFeedbackHandler;
            await this.onFeedbackReasonSubmit(event, chatFeedbackHandler);
        };

        alpineElement.removeEventListener(FEEDBACK_RATING_SUBMIT_EVENT, onFeedbackRatingSubmitHandler);
        alpineElement.addEventListener(FEEDBACK_RATING_SUBMIT_EVENT, onFeedbackRatingSubmitHandler);

        alpineElement.removeEventListener(FEEDBACK_REASON_SUBMIT_EVENT, onFeedbackReasonSubmitHandler);
        alpineElement.addEventListener(FEEDBACK_REASON_SUBMIT_EVENT, onFeedbackReasonSubmitHandler);
    }

    private async onFeedbackRatingSubmit(event: RatingEvent, chatFeedbackHandler: ChatFeedbackHandler|null): Promise<void>
    {
        if (null === chatFeedbackHandler) {
            return;
        }

        const { id, rating, messageId } = event.detail;

        if (messageId.trim() === "") {
            this.clientErrorLogger.logError('Feedback rating submit failed: messageId is empty');
            return;
        }

        await chatFeedbackHandler.sendFeedbackRating(id, messageId, rating, this.componentConfig.client);
    }

    private async onFeedbackReasonSubmit(event: ReasonEvent, chatFeedbackHandler: ChatFeedbackHandler|null): Promise<void>
    {
        if (null === chatFeedbackHandler) {
            return;
        }

        const { id, reason, freeText, messageId } = event.detail;

        if (messageId.trim() === "") {
            this.clientErrorLogger.logError('Feedback reason submit failed: messageId is empty');
            return;
        }

        await chatFeedbackHandler.sendFeedbackReason(id, messageId, reason, this.componentConfig.client, freeText);
    }

    private get ChatFeedbackHandler(): ChatFeedbackHandler|null {
        if(this.chatFeedbackHandler !== null) {
            return this.chatFeedbackHandler;
        }

        if(this.widget.activeChat instanceof BotChat) {
            this.chatFeedbackHandler = new ChatFeedbackHandler(this.widget.activeChat, this.widgetConfig.trademark);
            return this.chatFeedbackHandler;
        }

        return null;
    }

    public handleInvalidChatSessionError( errorType: string ): void
    {
        const alpineElement = this.getAlpineComponentElement();
        alpineElement?.dispatchEvent(new CustomEvent('trigger-system-error', {
            detail: {
                errorType: errorType,
            }
        }));
    }

    public resetSystemError( errorType: string ): void
    {
        const alpineElement = this.getAlpineComponentElement();
        alpineElement?.dispatchEvent(new CustomEvent('reset-system-error', {
            detail: {
                errorType: errorType,
            }
        }));
    }

    public setAnswerContent(chatType: string, responseData: ResponseObject): void
    {
        const alpineElement = this.getAlpineComponentElement();

        let answerHtml = responseData.answerHtml;
        if(responseData.isError && responseData.useDefaultHtml) {
            answerHtml = "Es konnte keine passende Antwort gefunden werden. Bitte versuchen Sie es erneut.";
        }

        alpineElement?.dispatchEvent(new CustomEvent('add-message', {
            detail: {
                text: answerHtml,
                id: 'msg-answer-' + this.answerCounter,
                type: "answer",
                isAgentChat: chatType === ChatType.AGENT_CHAT,
                author: responseData.author,
                time: responseData.time,
                messageId: responseData.messageId,
                responseId: responseData.responseId,
                isError: responseData.isError
            }
        }));

        this.answerCounter++;

    }

    public setAgentIsTyping(duration: number): void
    {
        const alpineElement = this.getAlpineComponentElement();
        alpineElement?.dispatchEvent(new CustomEvent('set-agent-typing', {
            detail: {
                duration: duration,
            }
        }));
    }

    public showBotSuggestions(): void
    {
        const alpineElement = this.getAlpineComponentElement();
        alpineElement?.dispatchEvent(new CustomEvent('show-bot-suggestions'));
    }

    public showBotGreeting(): void
    {
        const alpineElement = this.getAlpineComponentElement();
        alpineElement?.dispatchEvent(new CustomEvent('show-bot-greeting'));
    }

    public closeChatByAgent( leftByBot = false ): void
    {
        const alpineElement = this.getAlpineComponentElement();
        alpineElement?.dispatchEvent(new CustomEvent('close-chat-by-agent', {
            detail: {
                leftByBot: leftByBot,
            }
        }));
    }

    public closeChat( isError: boolean|null = null ): void
    {
        const alpineElement = this.getAlpineComponentElement();
        alpineElement?.dispatchEvent(new CustomEvent('close-chat', {
            detail: {
                isError: isError,
            }
        }));
    }

    public minimizeChat( ): void
    {
        const alpineElement = this.getAlpineComponentElement();
        alpineElement?.dispatchEvent(new CustomEvent('minimize-chat'));
    }

    public setHistoryContent(chatType: string, messageHistory:MessageHistory): void
    {
        const alpineElement = this.getAlpineComponentElement();
        if (alpineElement === null) {
            return;
        }

        const messagesCount = messageHistory.getMessages().length;
        messageHistory.getMessages().forEach((message, index) => {
            let type = 'question';

            if(message.sender === 'AGENT_MESSAGE') {
                type = 'answer';
            }

            const messageId = self.crypto.randomUUID();
            let eventDetail = {
                text: message.message,
                id: 'msg-' + type + '-' + messageId,
                type: type,
                author: message.senderName,
                time: message.time,
                messageId: message.messageId,
                responseId: message.responseId,
            };

            if (chatType === ChatType.BOT_CHAT) {
                alpineElement?.dispatchEvent(new CustomEvent('add-bot-history-message', {
                    detail: {
                        ...eventDetail,
                        isLastMessage: index === messagesCount-1,
                        feedbackState: message.feedbackState ?? null,
                    }
                }));
                return;
            }

            alpineElement?.dispatchEvent(new CustomEvent('add-message', {
                detail: {
                    ...eventDetail,
                    isAgentChat: chatType === ChatType.AGENT_CHAT,
                    isHistoryMessage: true
                }
            }));
        });

        Alpine.nextTick(() => {
            const scrollContainer = alpineElement.querySelector('[x-ref="scrollContainer"]') as HTMLDivElement;
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight; // Scroll ans Ende
            }
        });
    }

    public closeWidget()
    {
        let host = this.widgetDom.host;
        let parent = host.parentNode;
        let parentWrapper = parent?.parentNode;

        host.remove();

        if(parentWrapper instanceof HTMLElement && parentWrapper.id == "chat-component-wrapper") {
            parentWrapper.classList.add('hide');
        }
    }

    public changeMessageStatus( id: string, status: MessageSendState ): void
    {
        const alpineElement = this.getAlpineComponentElement();
        alpineElement?.dispatchEvent(new CustomEvent('change-message-status', {
            detail: {
                id: id,
                status: status,
            }
        }));
    }

    private getAlpineComponentElement()
    {
        if (this.alpineComponent === null) {
            this.alpineComponent = this.widgetDom.querySelector('[x-data="messageContent"]');
        }

        return this.alpineComponent;
    }

}